import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { apiGetJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { planIdToRank } from "@/lib/plan-entitlements";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/app/suscripcion")({
  component: SuscripcionPage,
  head: () => ({ meta: [{ title: "Suscripción — Structura Market" }] }),
});

interface PlanOpt { id: string; name: string; monthlyCents: number; annualCents: number }
type Mode = "monthly_recurring" | "monthly_oneshot" | "annual_oneshot";
type Gateway = "uala" | "payway" | "mercadopago" | "dlocal";

const MODE_LABEL: Record<Mode, string> = {
  monthly_recurring: "Mensual con débito automático",
  monthly_oneshot: "Mensual (pago manual cada mes)",
  annual_oneshot: "Anual (pago único, 20% off)",
};
function fmt(c: number) { return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(c / 100); }

interface SubscribeConfig {
  payway: { configured: boolean; publicKey?: string; apiBaseUrl?: string; environment?: string };
  uala: { configured: boolean; environment?: string };
  dlocal: { configured: boolean; environment?: string };
  mercadopago?: { configured: boolean; recurring?: boolean };
  plans: PlanOpt[];
  gatewayOrder: Gateway[];
  currentPlanId: string | null;
  current?: {
    planId: string | null;
    billingStatus: string | null;
    billingProvider: string | null;
    billingMode: string | null;
    amountCents: number | null;
    nextChargeAt: string | null;
    validUntil?: string | null;
    mpRecurringActive: boolean;
  };
  upgradeQuote?: {
    prorationCents: number;
    currentPlanId: string | null;
    targetPlanId: string;
    currentPlanCents: number;
    newPlanCents: number;
    periodEnd: string | null;
    remainingDays: number;
    recurringCents: number;
  } | null;
}

function SuscripcionPage() {
  const tid = useWorkspaceTenantId();
  const [data, setData] = useState<SubscribeConfig | null>(null);
  const { confirm } = useAppDialogs();
  const [planId, setPlanId] = useState("");
  const [mode, setMode] = useState<Mode>("monthly_oneshot");
  const [phase, setPhase] = useState<"loading" | "select" | "card" | "processing" | "done">("loading");
  // payway card fields
  const [num, setNum] = useState(""); const [name, setName] = useState("");
  const [expM, setExpM] = useState(""); const [expY, setExpY] = useState(""); const [cvv, setCvv] = useState(""); const [dni, setDni] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async (selectedPlan?: string) => {
    try {
      const q = selectedPlan ? `?quotePlanId=${encodeURIComponent(selectedPlan)}` : "";
      const res = await apiGetJson<SubscribeConfig>(`/api/billing/subscribe${q}`, tid);
      setData(res);
      const nextPlan = selectedPlan || res.currentPlanId || res.plans[0]?.id || "";
      setPlanId(nextPlan);
      if (res.current?.mpRecurringActive && !selectedPlan) setMode("monthly_recurring");
      setPhase("select");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Error"); setPhase("select"); }
  }, [tid]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("mp_sub") === "ok") {
      toast.success("Estamos confirmando tu suscripción. Se activará cuando Mercado Pago autorice el cobro.");
    }
  }, []);

  const plan = data?.plans.find((p) => p.id === planId);
  const amountCents = plan ? (mode === "annual_oneshot" ? plan.annualCents : plan.monthlyCents) : 0;
  const isPlanChange = Boolean(planId && data?.currentPlanId && planId !== data.currentPlanId);
  const upgradeQuote = data?.upgradeQuote && data.upgradeQuote.targetPlanId === planId ? data.upgradeQuote : null;
  const chargeCents = isPlanChange
    ? (upgradeQuote?.prorationCents ?? amountCents)
    : amountCents;
  // Upgrade mid-cycle: forzar oneshot del prorrateo (no recrear preapproval MP en el acto).
  const effectiveMode: Mode = isPlanChange ? "monthly_oneshot" : mode;
  const orderedGateways = data?.gatewayOrder ?? [];
  const mpRecurringOk = Boolean(data?.mercadopago?.recurring);
  const chosenGateway: Gateway | null =
    isPlanChange
      ? (orderedGateways.find((g) => g !== "mercadopago") ?? orderedGateways[0] ?? null)
      : mode === "monthly_recurring"
        ? (mpRecurringOk && orderedGateways.includes("mercadopago")
          ? "mercadopago"
          : (orderedGateways.includes("payway") ? "payway" : null))
        : (orderedGateways[0] ?? null);
  const currentPlan = data?.plans.find((p) => p.id === data.currentPlanId);
  const mpActive = Boolean(data?.current?.mpRecurringActive);

  const cancelMp = async () => {
    if (!(await confirm({ title: "¿Cancelar la suscripción?", description: "No se harán más cobros automáticos.", destructive: true, confirmLabel: "Cancelar suscripción" }))) return;
    setCancelling(true);
    try {
      const res = await fetch("/api/billing/mp-recurring/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Tenant-Id": tid ?? "" },
      });
      const d = await res.json() as { ok?: boolean; error?: string };
      if (d.ok) {
        toast.success("Suscripción cancelada");
        await load();
      } else {
        toast.error(d.error ?? "No se pudo cancelar");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setCancelling(false);
    }
  };

  const startSubscribe = async () => {
    if (!chosenGateway) { toast.error("No hay medios de pago configurados. Contactá al administrador."); return; }
    if (chargeCents < 0) { toast.error("Plan inválido"); return; }
    if (isPlanChange && chargeCents === 0) {
      setPhase("processing");
      try {
        const res = await fetch("/api/billing/subscribe", {
          method: "POST", headers: { "Content-Type": "application/json", "X-Tenant-Id": tid ?? "" },
          body: JSON.stringify({
            billingMode: effectiveMode,
            amountCents: 0,
            planId,
            provider: chosenGateway === "mercadopago" ? "uala" : chosenGateway,
            upgrade: true,
            recurringAmountCents: upgradeQuote?.recurringCents ?? amountCents,
          }),
        });
        const d = await res.json() as { ok?: boolean; error?: string };
        if (d.ok) { setPhase("done"); toast.success("Plan actualizado"); await load(planId); }
        else { toast.error(d.error ?? "No se pudo actualizar"); setPhase("select"); }
      } catch { toast.error("Error de conexión"); setPhase("select"); }
      return;
    }
    if (chargeCents <= 0) { toast.error("Plan inválido"); return; }
    if (chosenGateway === "payway" && !isPlanChange) { setPhase("card"); return; }
    if (chosenGateway === "payway" && isPlanChange) { setPhase("card"); return; }
    setPhase("processing");
    try {
      const res = await fetch("/api/billing/subscribe", {
        method: "POST", headers: { "Content-Type": "application/json", "X-Tenant-Id": tid ?? "" },
        body: JSON.stringify({
          billingMode: effectiveMode,
          amountCents: chargeCents,
          planId,
          provider: chosenGateway,
          upgrade: isPlanChange,
          recurringAmountCents: upgradeQuote?.recurringCents ?? amountCents,
        }),
      });
      const d = await res.json() as { ok?: boolean; checkoutUrl?: string; error?: string };
      if (d.ok && d.checkoutUrl) { window.location.href = d.checkoutUrl; return; }
      toast.error(d.error ?? "No se pudo iniciar el pago"); setPhase("select");
    } catch { toast.error("Error de conexión"); setPhase("select"); }
  };

  const submitPaywayCard = async (e: React.FormEvent) => {
    e.preventDefault();
    const pw = data?.payway;
    if (!pw?.configured || !pw.publicKey || !pw.apiBaseUrl) { toast.error("Payway no disponible"); return; }
    setPhase("processing");
    try {
      const tokRes = await fetch(`${pw.apiBaseUrl}/tokens`, {
        method: "POST", headers: { apikey: pw.publicKey, "Content-Type": "application/json" },
        body: JSON.stringify({ card_number: num.replace(/\s/g, ""), card_expiration_month: expM.padStart(2, "0"), card_expiration_year: expY.slice(-2), security_code: cvv, card_holder_name: name, card_holder_identification: { type: "dni", number: dni } }),
      });
      const tok = await tokRes.json();
      if (!tokRes.ok || !tok.id) { toast.error("Tarjeta inválida"); setPhase("card"); return; }
      const res = await fetch("/api/billing/subscribe", {
        method: "POST", headers: { "Content-Type": "application/json", "X-Tenant-Id": tid ?? "" },
        body: JSON.stringify({
          billingMode: effectiveMode,
          amountCents: chargeCents,
          planId,
          token: tok.id,
          bin: tok.bin,
          paymentMethodId: 1,
          provider: "payway",
          upgrade: isPlanChange,
          recurringAmountCents: upgradeQuote?.recurringCents ?? amountCents,
        }),
      });
      const d = await res.json();
      if (d.ok) { setPhase("done"); toast.success("¡Suscripción activada!"); }
      else { toast.error(d.error ?? "Pago rechazado"); setPhase("card"); }
    } catch { toast.error("Error de conexión"); setPhase("card"); }
  };

  if (phase === "loading") {
    return (
      <div><DashboardHeader title="Suscripción" />
        <ClinicalPageShell><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></ClinicalPageShell>
      </div>
    );
  }
  if (phase === "done") {
    return (
      <div><DashboardHeader title="Suscripción" />
        <ClinicalPageShell maxWidth={false} className="max-w-xl">
          <Card><CardContent className="py-10 text-center">
            <div className="text-5xl">✅</div>
            <h2 className="mt-3 text-xl font-bold text-emerald-600">Suscripción activada</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Tu plan quedó activo. {mode === "monthly_recurring" ? "Se renovará automáticamente cada mes." : "Te avisaremos antes del vencimiento."}
            </p>
          </CardContent></Card>
        </ClinicalPageShell>
      </div>
    );
  }

  const noGateways = orderedGateways.length === 0;

  return (
    <div>
      <DashboardHeader title="Suscripción" subtitle="Elegí o cambiá tu plan" />
      <ClinicalPageShell maxWidth={false} className="max-w-3xl space-y-6">
        {noGateways && (
          <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-600">
            No hay medios de pago habilitados. El administrador los configura en Planes → Alta online.
          </p>
        )}
        {data?.current?.billingStatus === "past_due" && (
          <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700">
            El último cobro no se pudo completar. Tu cuenta sigue activa durante el período de gracia.
          </p>
        )}

        {/* Paso: selección de plan (todos los planes, actual marcado) */}
        {phase === "select" && (
          <>
            {mpActive && currentPlan && (
              <Card>
                <CardHeader>
                  <CardTitle>Plan actual</CardTitle>
                  <CardDescription>Mercado Pago</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xl font-bold">{currentPlan.name}</p>
                  <p className="text-2xl font-bold">{fmt(data?.current?.amountCents ?? currentPlan.monthlyCents)} <span className="text-sm font-normal text-muted-foreground">/ mes</span></p>
                  <p className="text-sm">🟢 Suscripción activa</p>
                  {data?.current?.nextChargeAt && (
                    <p className="text-sm text-muted-foreground">
                      Próximo cobro: {data.current.nextChargeAt.slice(8, 10)}/{data.current.nextChargeAt.slice(5, 7)}/{data.current.nextChargeAt.slice(0, 4)}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">Método: Mercado Pago</p>
                  <p className="text-sm text-muted-foreground">Tu suscripción se renovará automáticamente cada mes.</p>
                  <Button variant="outline" disabled={cancelling} onClick={() => void cancelMp()}>
                    {cancelling ? "Cancelando…" : "Cancelar suscripción"}
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data?.plans.map((p) => {
                const isCurrent = p.id === data?.currentPlanId;
                const selected = p.id === planId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setPlanId(p.id); void load(p.id); }}
                    className={cn(
                      "relative rounded-xl border p-5 text-left transition-all",
                      selected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card hover:border-primary/40",
                    )}
                  >
                    {isCurrent && (
                      <span className="absolute -top-2.5 left-4 rounded-full bg-emerald-500 px-2.5 py-0.5 text-[10px] font-bold text-white uppercase tracking-wider">
                        Tu plan actual
                      </span>
                    )}
                    {selected && <Check className="absolute right-3 top-3 h-4 w-4 text-primary" />}
                    <p className="font-bold text-foreground">{p.name}</p>
                    <p className="mt-2 text-2xl font-bold text-foreground">
                      {fmt(p.monthlyCents)}<span className="text-sm font-normal text-muted-foreground">/mes</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">o {fmt(p.annualCents)}/año</p>
                  </button>
                );
              })}
            </div>

            <Card>
              <CardHeader><CardTitle>Modalidad de cobro</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <select
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  value={isPlanChange ? "monthly_oneshot" : mode}
                  disabled={isPlanChange}
                  onChange={(e) => setMode(e.target.value as Mode)}
                >
                  {(Object.keys(MODE_LABEL) as Mode[]).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
                </select>
                {isPlanChange ? (
                  <p className="text-xs text-muted-foreground">
                    En un cambio de plan cobramos solo el prorrateo ahora; el próximo ciclo usa el precio del plan nuevo.
                  </p>
                ) : null}
                {isPlanChange && upgradeQuote ? (
                  <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 text-sm space-y-1">
                    <p className="font-medium text-foreground">Upgrade con prorrateo</p>
                    <p className="text-muted-foreground">
                      Plan actual {fmt(upgradeQuote.currentPlanCents)} → nuevo {fmt(upgradeQuote.newPlanCents)}.
                      Quedan ~{upgradeQuote.remainingDays} días del ciclo
                      {upgradeQuote.periodEnd ? ` (hasta ${upgradeQuote.periodEnd})` : ""}.
                    </p>
                    <p className="text-muted-foreground">
                      Hoy cobramos la diferencia de los días restantes:{" "}
                      <span className="font-semibold text-foreground">{fmt(upgradeQuote.prorationCents)}</span>.
                      Después, la recurrencia pasa a {fmt(upgradeQuote.recurringCents)}/mes.
                    </p>
                  </div>
                ) : null}
                <div className="flex items-center justify-between border-t pt-3">
                  <p className="text-lg font-bold">
                    {planId && data?.currentPlanId && planId === data.currentPlanId
                      ? "Plan actual"
                      : isPlanChange
                        ? `Total ahora (prorrateo): ${fmt(chargeCents)}`
                        : `Total ahora: ${fmt(amountCents)}`}
                  </p>
                  {planId && data?.currentPlanId && planId === data.currentPlanId ? (
                    <Button disabled variant="secondary">Ya es tu plan</Button>
                  ) : (
                    <Button
                      disabled={noGateways || !chosenGateway || (chargeCents <= 0 && !isPlanChange)}
                      onClick={() => void startSubscribe()}
                    >
                      {(() => {
                        const sel = planIdToRank(planId);
                        const cur = planIdToRank(data?.currentPlanId);
                        if (isPlanChange && chargeCents === 0) return "Aplicar cambio de plan";
                        if (sel < cur) return "Cambiar a este plan";
                        if (isPlanChange) return "Pagar diferencia y upgradear";
                        if (chosenGateway === "mercadopago") return "Suscribirme con Mercado Pago";
                        if (chosenGateway === "payway") return "Continuar al pago";
                        if (chosenGateway === "dlocal") return "Pagar con dLocal";
                        return "Pagar con Ualá";
                      })()}
                    </Button>
                  )}
                </div>
                {mode === "monthly_recurring" && chosenGateway === "mercadopago" && (
                  <p className="text-sm text-muted-foreground">Tu suscripción se renovará automáticamente cada mes.</p>
                )}
                {mode === "monthly_recurring" && !chosenGateway && (
                  <p className="text-xs text-amber-600">El débito automático no está habilitado. Elegí pago mensual o anual, o contactá al administrador.</p>
                )}
                {chosenGateway && (
                  <p className="text-[11px] text-muted-foreground">
                    Se cobra con {chosenGateway === "uala" ? "Ualá Bis" : chosenGateway === "mercadopago" ? "Mercado Pago" : chosenGateway === "dlocal" ? "dLocal Go" : "Payway"} (definido por la plataforma).
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Paso: tarjeta Payway (solo si el gateway elegido es payway) */}
        {phase === "card" && data?.payway.configured && (
          <Card>
            <CardHeader>
              <CardTitle>Datos de la tarjeta</CardTitle>
              {data.payway.environment === "sandbox" && <CardDescription className="text-amber-600">Modo prueba: 4507 9900 0000 4905, 12/50, CVV 123</CardDescription>}
            </CardHeader>
            <CardContent>
              <form onSubmit={submitPaywayCard} className="space-y-3">
                <Input required value={num} onChange={(e) => setNum(e.target.value)} placeholder="Número de tarjeta" inputMode="numeric" />
                <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre en la tarjeta" />
                <div className="grid grid-cols-3 gap-2">
                  <Input required value={expM} onChange={(e) => setExpM(e.target.value)} placeholder="MM" maxLength={2} />
                  <Input required value={expY} onChange={(e) => setExpY(e.target.value)} placeholder="AA" maxLength={4} />
                  <Input required value={cvv} onChange={(e) => setCvv(e.target.value)} placeholder="CVV" maxLength={4} />
                </div>
                <Input required value={dni} onChange={(e) => setDni(e.target.value)} placeholder="DNI del titular" inputMode="numeric" />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setPhase("select")}>Atrás</Button>
                  <Button type="submit" className="flex-1">{isPlanChange ? `Pagar prorrateo — ${fmt(chargeCents)}` : `Suscribirme — ${fmt(amountCents)}`}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {phase === "processing" && (
          <Card><CardContent className="flex items-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Procesando…
          </CardContent></Card>
        )}
      </ClinicalPageShell>
    </div>
  );
}
